<template>
  <div class="custom-column-menu" data-cy="custom-column-menu">
    <GridColumnMenuSort
      :column="column"
      :sortable="sortable"
      :sort="sort"
      @closemenu="closeMenu"
      @sortchange="sortChange"
    />
    <GridColumnMenuFilter
      v-if="!currentColumn.onlyValueFilter"
      :column="column"
      :filterable="filterable"
      :filter="filter"
      :filter-operators="columnOperators"
      @filterfocus="handleFocus"
      @closemenu="closeMenu"
      @expandchange="expandChange"
      @filterchange="filterChange"
    />
    <GridColumnMenuCheckboxFilter
      v-bind="(gridColumnMenuCheckboxFilterProps as any)"
      @filterchange="filterChange"
      @expandchange="itemsExpandChange"
      @closemenu="closeMenu"
    />
    <GridColumnMenuItemGroup v-if="!hideColumnSelection">
      <GridColumnMenuItem
        :title="'Columns'"
        :icon-class="'k-i-columns'"
        @menuitemclick="onMenuItemClick"
      />
      <GridColumnMenuItemContent :show="columnsExpanded">
        <div class="k-column-list-wrapper">
          <form @submit="onSubmit" @reset="onReset">
            <div class="k-column-list">
              <div
                :key="idx"
                class="k-column-list-item"
                v-for="(column, idx) in currentColumns
                  .filter((c) => !c.hideInColumnMenu)
                  .sort((a, b) => a.orderIndex - b.orderIndex)"
              >
                <span>
                  <input
                    :id="'column-visiblity-show-' + idx"
                    :class="'k-checkbox k-checkbox-md k-rounded-md'"
                    :type="'checkbox'"
                    :readOnly="true"
                    :disabled="!column.hidden && oneVisibleColumn"
                    :checked="!column.hidden"
                    @click="onToggleColumn(column)"
                  />
                  <label
                    :for="'column-visiblity-show-' + idx"
                    :class="'k-checkbox-label'"
                    :style="{ userSelect: 'none' }"
                  >
                    {{ column.title }} - {{ idx }}
                  </label>
                </span>
              </div>
            </div>
            <div class="k-columnmenu-actions">
              <Button type="reset">Reset</Button>
              <Button :theme-color="'primary'">Save</Button>
            </div>
          </form>
        </div>
      </GridColumnMenuItemContent>
    </GridColumnMenuItemGroup>
  </div>
</template>
<script setup lang="ts">
import {
  GridColumnMenuFilter,
  GridColumnMenuSort,
  GridColumnMenuItemGroup,
  GridColumnMenuItemContent,
  GridColumnMenuItem,
  GridColumnMenuCheckboxFilter,
} from "@progress/kendo-vue-grid";
import { computed, onMounted, ref } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-vue-buttons";
import {
  getFilterOperatorsForBoolean,
  getFilterOperatorsForDate,
  getFilterOperatorsForNumber,
  getFilterOperatorsForString,
} from "@/shared/helpers/filtersHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const props = defineProps<{
  column: GridColumnPropsExtended;
  sortable: boolean | object;
  sort: SortDescriptor[];
  filter?: CompositeFilterDescriptor;
  filterable: boolean;
  columns: any[];
  items?: any[];
  getItems?: (column: GridColumnPropsExtended) => Promise<any[]>;
  hideColumnSelection?: boolean;
}>();

const emit = defineEmits<{
  (event: "closemenu", ...args: any[]): void;
  (event: "sortchange", newDescriptor: SortDescriptor[], e: any): void;
  (
    event: "filterchange",
    filter: CompositeFilterDescriptor | null,
    syntheticEvent: any
  ): void;
  (event: "expandchange", nextExpandState: boolean): void;
  (event: "filterfocus", ...args: any[]): void;
  (event: "contentfocus", ...args: any[]): void;
  (event: "columnssubmit", ...args: any[]): void;
}>();

const currentColumns = ref<any[]>(props.columns);
const columnsExpanded = ref<boolean>(false);
const filterExpanded = ref<boolean>(false);
const uniqueData = ref<boolean>(true);
const items = ref<any[]>(props.items || []);
const itemsUpdated = ref<boolean>(props.getItems === undefined);

const operators = {
  text: getFilterOperatorsForString(),
  numeric: getFilterOperatorsForNumber(),
  date: getFilterOperatorsForDate(),
  boolean: getFilterOperatorsForBoolean(),
};

const gridColumnMenuCheckboxFilterProps = computed(() => {
  return {
    column: props.column,
    filter: props.filter,
    filterable: props.filterable,
    filterOperators: operators,
    uniqueData: uniqueData.value,
    searchBox: true,
    dataItems: items.value,
    class: "custom-column-menu-checkbox-filter",
  };
});

const currentColumn = computed(() => {
  return props.columns.find(
    (c) => c.field === props.column.field
  ) as GridColumnPropsExtended;
});

const columnOperators = computed(() => {
  if (!currentColumn.value || !currentColumn.value.allowedFilterOperators)
    return operators;
  return {
    text: currentColumn.value.allowedFilterOperators,
    numeric: currentColumn.value.allowedFilterOperators,
    date: currentColumn.value.allowedFilterOperators,
    boolean: currentColumn.value.allowedFilterOperators,
  };
});

const oneVisibleColumn = computed(() => {
  return currentColumns.value.filter((c) => !c.hidden).length === 1;
});

const handleFocus = function (e: any[]) {
  emit("contentfocus", e);
};
const onToggleColumn = function (column: any) {
  currentColumns.value = currentColumns.value.map((col, idx) => {
    return col === column ? { ...col, hidden: !col.hidden } : col;
  });
};

const onReset = function (event: Event) {
  event.preventDefault();
  const allColumns = props.columns.map((col) => {
    return {
      ...col,
      hidden: false,
    };
  });

  currentColumns.value = allColumns;
  onSubmit();
};
const onSubmit = function (event?: Event) {
  if (event) {
    event.preventDefault();
  }
  emit("columnssubmit", currentColumns.value);
  emit("closemenu", event);
};
const onMenuItemClick = function () {
  const value = !columnsExpanded.value;
  columnsExpanded.value = value;
  filterExpanded.value = value ? false : filterExpanded.value;
};

const itemsExpandChange = async function (nextExpandState: boolean) {
  if (!itemsUpdated.value && props.getItems) {
    items.value = await props.getItems(props.column);
    itemsUpdated.value = true;
  }
  expandChange(nextExpandState);
};

const expandChange = function (nextExpandState: boolean) {
  emit("expandchange", nextExpandState);
};

const closeMenu = function () {
  emit("closemenu");
};

const filterChange = function (
  newDescriptor: CompositeFilterDescriptor | null,
  e: any
) {
  emit("filterchange", newDescriptor, e);
};

const sortChange = function (newDescriptor: SortDescriptor[], e: any) {
  emit("sortchange", newDescriptor, e);
};

onMounted(() => {
  // Change the name of value filter
  const popups = document.querySelectorAll(".k-grid-columnmenu-popup");
  Array.from(popups).forEach((popup) => {
    const menuItems = popup.querySelectorAll(".k-columnmenu-item");
    const filterItems: HTMLDivElement[] = Array.from(menuItems).filter(
      (item) => (item as HTMLDivElement).innerText === "Filter"
    ) as HTMLDivElement[];

    // Check if name have been already changed
    if (filterItems.length < 2) return;

    const lastFilterItem = filterItems[filterItems.length - 1];
    if (lastFilterItem) {
      lastFilterItem.innerHTML = lastFilterItem.innerHTML.replace(
        "Filter",
        "Value Filter"
      );
    }
  });
});
</script>

<style lang="scss">
.custom-column-menu-checkbox-filter {
  .k-child-animation-container {
    max-height: unset !important;
  }
}
.custom-column-menu {
  .k-checkbox-label {
    white-space: pre;
  }
}
</style>
